import { Status } from '@/common/enums/Status';
import { SmsDeliveryStatus } from '@/common/enums/SmsDeliveryStatus';
import {
	ListTabType,
	OfferTransportList,
	TableColumn,
	TransportListColumns,
} from '@/new-transport/_models/transport-list.model';
import { TransportSide } from '@/new-transport/_models/transport.model';
import { Slot, SlotFlight } from '@/reservation/reservation.model';
import { DatePipe, NgStyle } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	QueryList,
	viewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { PricePipe } from '../../../common/pipes/price.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { StatusLabelPipe } from '../../../common/pipes/status-label.pipe';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { SmsGenerateDialogComponent } from '../sms-generate-dialog/sms-generate-dialog.component';
import { catchError, EMPTY, filter } from 'rxjs';
import { TransportListApiService } from '../_api/transport-list.api.service';
import { UserDocumentNumbersPipe } from './_pipes/user-document-numbers.pipe';
import { SmsDeliveryStatusClassPipe } from './_pipes/sms-delivery-status-class.pipe';

const SERVICE_TYPE_CANCEL_INSURANCE_ID = 9;

@Component({
	selector: 'app-transport-list-table',
	standalone: true,
	imports: [
		MatTableModule,
		NgStyle,
		RouterModule,
		PricePipe,
		DatePipe,
		MatProgressSpinnerModule,
		MatButtonModule,
		MatSortModule,
		StatusLabelPipe,
		MatCheckboxModule,
		MatTooltipModule,
		FormsModule,
		MatFormFieldModule,
		MatInputModule,
		UserDocumentNumbersPipe,
		SmsDeliveryStatusClassPipe,
	],
	providers: [
		StatusLabelPipe,
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
	templateUrl: './transport-list-table.component.html',
	styleUrl: './transport-list-table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransportListTableComponent implements AfterViewInit, OnDestroy {
	readonly columns = input.required<TableColumn[]>();
	readonly data = input.required<OfferTransportList[]>();
	readonly transportSide = input.required<TransportSide>();
	readonly tabType = input.required<ListTabType>();

	readonly refreshView = output<void>();

	readonly ListTabType = ListTabType;
	readonly SmsDeliveryStatus = SmsDeliveryStatus;

	readonly selection = new SelectionModel<OfferTransportList>(true, []);

	readonly TransportSide = TransportSide;

	readonly dataSource = computed(() => {
		const table = new MatTableDataSource<OfferTransportList>(this.data());
		this.setSorting(table);
		return table;
	});

	readonly tableWidth = computed(() => {
		const totalWidth = this.columns().reduce((sum, column) => sum + parseInt(column.width), 0);
		const tableElement = document.querySelector('app-transport-list-table') as HTMLElement;
		if (tableElement) {
			const { offsetWidth } = tableElement;
			if (offsetWidth > totalWidth) return '100%';
			return totalWidth + 'px';
		} else return '0px';
	});

	readonly displayedColumns = computed(() => {
		let columns = [];
		if (this.columns) columns = this.columns().map((item) => item.field);
		return columns;
	});

	private static readonly CHECKBOX_DATA_COLUMNS: ReadonlySet<TransportListColumns> = new Set<TransportListColumns>([
		'isDataEntered',
		'isDepartureCheckIn',
		'isDepartureSent',
		'isReturnCheckIn',
		'isReturnSent',
		'kr',
	]);

	private static readonly TEXT_INPUT_DATA_COLUMNS: ReadonlySet<TransportListColumns> = new Set<TransportListColumns>([
		'departureTicket',
		'returnTicket',
	]);

	readonly isLoadingResults = computed(() => !this.data() || this.data().length === 0);

	private observer: MutationObserver;

	private readonly sort = viewChild(MatSort);

	private readonly el = inject(ElementRef);
	private readonly _snackBar = inject(MatSnackBar);
	private readonly statusLabelPipe = inject(StatusLabelPipe);
	private readonly matDialog = inject(MatDialog);
	private readonly transportListApiService = inject(TransportListApiService);

	ngAfterViewInit(): void {
		this.observeMatTable();
		this.setSorting(this.dataSource());
	}

	ngOnDestroy(): void {
		this.observer.disconnect();
	}

	observeMatTable(): void {
		this.observer = new MutationObserver(() => {
			this.columns().forEach((column) => {
				if (column.width) {
					this.setColumnStyle(column.field, column.width);
				}
			});
		});

		const nodes = document.querySelectorAll('.mat-mdc-tab-body-content');

		nodes.forEach((node) => {
			this.observer.observe(node, {
				attributes: true,
				childList: true,
				characterData: true,
				subtree: true,
			});
		});
	}

	async copyTable(headers: string[], data: string[][]): Promise<void> {
		const table = [headers, ...data];
		const csvData = table.map((row) => row.join(';')).join('\n');
		await navigator.clipboard.writeText(csvData);
		this._snackBar.open('Skopiowano do schowka!', 'OK', { duration: 2000 });
	}

	copyToClipboard(): void {
		const displayedColumns = this.getDisplayedColumns('mat-cell');
		const headers = this.getDisplayedColumns('mat-header-cell');
		const convertedColumns = [];
		for (let i = 0; i < displayedColumns.length; i += headers.length) {
			convertedColumns.push(displayedColumns.slice(i, i + headers.length));
		}
		this.copyTable(headers, convertedColumns);
	}

	checkPackageComfort(slot: Slot): boolean {
		return !!slot.slotServices.find((item) => item.service.code.includes('PAK'));
	}

	hasCancellationInsuranceService(slot: Slot): boolean {
		return !!slot.slotServices?.find((item) => item.service.serviceType?.id === SERVICE_TYPE_CANCEL_INSURANCE_ID);
	}

	patchFlightField(row: OfferTransportList, field: keyof SlotFlight, value: boolean): void {
		const flight = this.ensureFlight(row);
		const previousValue = !value;
		const previousReportedAt = flight.cancellationInsuranceReportedAt;
		(flight[field] as boolean) = value;
		this.transportListApiService
			.patchSlotFlight(row.slot.id, { [field]: value })
			.pipe(
				catchError(() => {
					(flight[field] as boolean) = previousValue;
					flight.cancellationInsuranceReportedAt = previousReportedAt;
					this._snackBar.open('Nie udało się zapisać zmiany', 'OK', { duration: 3000 });
					return EMPTY;
				}),
			)
			.subscribe((response) => {
				if (field === 'isCancellationInsuranceReported' && response.flight) {
					flight.cancellationInsuranceReportedAt = response.flight.cancellationInsuranceReportedAt;
				}
				this._snackBar.open('Zapisano zmianę', 'OK', { duration: 2000 });
			});
	}

	patchTicket(row: OfferTransportList, field: 'departureTicket' | 'returnTicket', value: string): void {
		const flight = this.ensureFlight(row);
		const trimmed = value.trim();
		if ((flight[field] ?? '') === trimmed) {
			return;
		}
		const previousValue = flight[field];
		flight[field] = trimmed;
		this.transportListApiService
			.patchSlotFlight(row.slot.id, { [field]: trimmed })
			.pipe(
				catchError(() => {
					flight[field] = previousValue;
					this._snackBar.open('Nie udało się zapisać numeru biletu', 'OK', { duration: 3000 });
					return EMPTY;
				}),
			)
			.subscribe(() => {
				this._snackBar.open('Zapisano numer biletu', 'OK', { duration: 2000 });
			});
	}

	isAllSelected(): boolean {
		const numSelected = this.selection.selected.length;
		const numRows = this.dataSource().data.length;
		return numSelected === numRows;
	}

	toggleAllRows(): void {
		if (this.isAllSelected()) {
			this.selection.clear();
			return;
		}

		this.selection.select(...this.dataSource().data);
	}

	smsDialogOpen(): void {
		this.matDialog
			.open<SmsGenerateDialogComponent, number[]>(SmsGenerateDialogComponent, {
				data: this.selection.selected.map((offer) => offer.slot.id),
				width: '50%',
			})
			.afterClosed()
			.pipe(filter(Boolean))
			.subscribe(() => {
				this.selection.clear();
				this.refreshView.emit();
			});
	}

	private ensureFlight(row: OfferTransportList): SlotFlight {
		if (!row.slot.flight) {
			row.slot.flight = {
				departureTicket: null,
				returnTicket: null,
				isDataEntered: false,
				isDepartureCheckIn: false,
				isDepartureSent: false,
				isReturnCheckIn: false,
				isReturnSent: false,
				isCancellationInsuranceReported: false,
				cancellationInsuranceReportedAt: null,
			};
		}
		return row.slot.flight;
	}

	private getDisplayedColumns(selector: string): string[] {
		const columnDefs = document.querySelectorAll(selector) as NodeListOf<HTMLElement>;
		const isDataCellSelector = selector === 'mat-cell';

		return Array.from(columnDefs).map((columnDef) => {
			if (isDataCellSelector && this.isCheckboxDataCell(columnDef)) {
				return this.extractCheckboxCellText(columnDef);
			}
			if (isDataCellSelector && this.isTextInputDataCell(columnDef)) {
				return this.extractTextInputCellText(columnDef);
			}
			return columnDef.innerText;
		});
	}

	private isCheckboxDataCell(cell: HTMLElement): boolean {
		for (const column of TransportListTableComponent.CHECKBOX_DATA_COLUMNS) {
			if (cell.classList.contains(`mat-column-${column}`)) return true;
		}
		return false;
	}

	private isTextInputDataCell(cell: HTMLElement): boolean {
		for (const column of TransportListTableComponent.TEXT_INPUT_DATA_COLUMNS) {
			if (cell.classList.contains(`mat-column-${column}`)) return true;
		}
		return false;
	}

	private extractTextInputCellText(cell: HTMLElement): string {
		const input = cell.querySelector('input:not([type=checkbox])') as HTMLInputElement | null;
		return input?.value.trim() ?? '';
	}

	private extractCheckboxCellText(cell: HTMLElement): string {
		const checkbox = cell.querySelector('input[type=checkbox]') as HTMLInputElement | null;
		const checkedToken = checkbox?.checked ? 'X' : '';
		const extraNode = cell.querySelector('.ms-2');
		let extra = extraNode?.textContent?.trim() ?? '';
		if (extra === '—') extra = '';
		if (checkedToken && extra) return `${checkedToken} ${extra}`;
		return checkedToken || extra;
	}

	private setColumnStyle(columnName: string, width: string): void {
		const matColumns: QueryList<HTMLElement> = this.el.nativeElement.querySelectorAll(`.mat-column-${columnName}`);

		if (matColumns) {
			matColumns.forEach((column) => {
				column.style.width = width;
				column.style.flex = `0 0 ${width}`;
			});
		}
	}

	private setSorting(dataSource: MatTableDataSource<OfferTransportList>): void {
		dataSource.sort = this.sort();
		dataSource.sortingDataAccessor = (element, property: TransportListColumns) => {
			if (
				(this.transportSide() === TransportSide.DEPARTURE && !element.slot.stopDeparture) ||
				(this.transportSide() === TransportSide.RETURN && !element.slot.stopReturn)
			) {
				return;
			}

			switch (property) {
				case 'stopDeparture':
					return this.transportSide() === TransportSide.DEPARTURE
						? element.slot.stopDeparture?.stop.location.name
						: element.slot.stopReturn?.stop.location.name;
				case 'status':
					return this.statusLabelPipe.transform(element.slot.status as Status);
				case 'unique':
					return element.reservation.id + element.slot.userData?.firstName + element.slot.userData?.lastName;
				case 'bc':
					return element.reservation.id;
				case 'firstName':
					return element.slot.userData
						? element.slot.userData.firstName
						: element.reservation.ownerUser.firstName;
				case 'lastName':
					return element.slot.userData
						? element.slot.userData.lastName
						: element.reservation.ownerUser.lastName;
				case 'slotCount':
					return element.reservationSlotCount;
				case 'seats':
					const { singleDeckDeparture, singleDeckReturn, doubleDeckDeparture, doubleDeckReturn } =
						element.slot.seatnumber;
					return `${singleDeckDeparture}/${singleDeckReturn}/${doubleDeckDeparture}/${doubleDeckReturn}`;
				case 'comfort':
					return this.checkPackageComfort(element.slot) ? 'PAK' : '';
				case 'phone':
					if (element.slot.userData && element.slot.userData.userContacts[0]?.phone) {
						return element.slot.userData?.userContacts[0]?.phone;
					}
					return element.reservation.ownerUser?.userContacts[0]?.phone;
				case 'product':
					return element.product.name;
				case 'note':
					return element.notes;
				case 'board':
					return element.board.name;
				case 'birth':
					return new Date(element.slot.userData?.birth);
				case 'surchargeCost':
					return element?.surchargeFee;
				case 'address':
					return element.slot.stopDeparture?.stop.address;
				case 'stopDepartureDate':
					return new Date(element.slot.stopDeparture?.timeDeparture);
				case 'stopDepartureHour':
					return new Date(element.slot.stopDeparture?.timeDeparture);
				case 'insurer':
					return element.product?.insurer?.name;
				default:
					return element[property];
			}
		};
	}
}
